import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store/authStore';
import './i18n';

// Public
import Home                   from './pages/public/Home';
import Login                  from './pages/auth/Login';
import PublicWorkerCard        from './pages/public/PublicWorkerCard';

// Worker
import WorkerDashboard        from './pages/worker/Dashboard';
import WorkerProfile          from './pages/worker/WorkerProfile';
import ProfileCreate          from './pages/worker/ProfileCreate';
import WorkerJobs             from './pages/worker/Jobs';
import WorkerJobDetail        from './pages/worker/WorkerJobDetail';
import WorkerMatches          from './pages/worker/WorkerMatches';
import WorkerApplications     from './pages/worker/WorkerApplications';
import WorkerPayment          from './pages/worker/Payment';
import WorkerDigitalCard      from './pages/worker/WorkerDigitalCard';

// Employer
import EmployerDashboard      from './pages/employer/Dashboard';
import EmployerProfileCreate  from './pages/employer/EmployerProfileCreate';
import EmployerProfile        from './pages/employer/EmployerProfile';
import EmployerRequirements   from './pages/employer/EmployerRequirements';
import EmployerRequirementDetail from './pages/employer/EmployerRequirementDetail';
import EmployerApplicants     from './pages/employer/EmployerApplicants';
import RequirementForm        from './pages/employer/RequirementForm';
import EmployerWorkerSearch   from './pages/employer/EmployerWorkerSearch';
import SavedWorkers           from './pages/employer/SavedWorkers';

// Admin
import AdminDashboard         from './pages/admin/Dashboard';
import AdminWorkers           from './pages/admin/Workers';
import AdminEmployers         from './pages/admin/Employers';
import AdminRequirements      from './pages/admin/Requirements';
import AdminPayments          from './pages/admin/Payments';
import AdminNotifications     from './pages/admin/Notifications';
import AdminMatchingWeights   from './pages/admin/MatchingWeights';
import AdminSettings          from './pages/admin/Settings';
import AuditLogs              from './pages/admin/AuditLogs';

function RequireAuth({ children, roles }) {
  const user  = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.accessToken);

  if (!token || !user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={`/${user.role}/dashboard`} replace />;
  return children;
}

const W  = (roles, El) => <RequireAuth roles={roles}>{El}</RequireAuth>;
const WK = (El) => W(['worker'], El);
const EM = (El) => W(['employer'], El);
const AD = (El) => W(['admin', 'superadmin'], El);

export default function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
      <Routes>
        {/* Public */}
        <Route path="/"      element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/w/:regNumber" element={<PublicWorkerCard />} />

        {/* Worker */}
        <Route path="/worker/dashboard"         element={WK(<WorkerDashboard />)} />
        <Route path="/worker/profile"           element={WK(<WorkerProfile />)} />
        <Route path="/worker/profile/create"    element={WK(<ProfileCreate />)} />
        <Route path="/worker/jobs"              element={WK(<WorkerJobs />)} />
        <Route path="/worker/jobs/:id"          element={WK(<WorkerJobDetail />)} />
        <Route path="/worker/matches"           element={WK(<WorkerMatches />)} />
        <Route path="/worker/applications"      element={WK(<WorkerApplications />)} />
        <Route path="/worker/payment/:type"     element={WK(<WorkerPayment />)} />
        <Route path="/worker/card"              element={WK(<WorkerDigitalCard />)} />

        {/* Employer */}
        <Route path="/employer/dashboard"                        element={EM(<EmployerDashboard />)} />
        <Route path="/employer/profile/create"                   element={EM(<EmployerProfileCreate />)} />
        <Route path="/employer/profile"                          element={EM(<EmployerProfile />)} />
        <Route path="/employer/requirements"                     element={EM(<EmployerRequirements />)} />
        <Route path="/employer/requirements/new"                 element={EM(<RequirementForm />)} />
        <Route path="/employer/requirements/:id"                 element={EM(<EmployerRequirementDetail />)} />
        <Route path="/employer/requirements/:id/applicants"      element={EM(<EmployerApplicants />)} />
        <Route path="/employer/workers/search"                   element={EM(<EmployerWorkerSearch />)} />
        <Route path="/employer/workers/saved"                    element={EM(<SavedWorkers />)} />

        {/* Admin */}
        <Route path="/admin/dashboard"      element={AD(<AdminDashboard />)} />
        <Route path="/admin/workers"        element={AD(<AdminWorkers />)} />
        <Route path="/admin/employers"      element={AD(<AdminEmployers />)} />
        <Route path="/admin/requirements"   element={AD(<AdminRequirements />)} />
        <Route path="/admin/payments"       element={AD(<AdminPayments />)} />
        <Route path="/admin/notifications"  element={AD(<AdminNotifications />)} />
        <Route path="/admin/matching"       element={AD(<AdminMatchingWeights />)} />
        <Route path="/admin/settings"       element={AD(<AdminSettings />)} />
        <Route path="/admin/audit-logs"     element={AD(<AuditLogs />)} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
